import { createRouter, createWebHistory } from 'vue-router';
import { clinicRoutes } from './clinic';
import { patientRoutes } from './patient';
import { adminRoutes } from './admin';
import liff from '@line/liff';
import { pinia } from '@/store';
import { useEstimateStore } from '@/store/estimate.store';
import { isWebAccess } from '@/utils/accessMode';

// 見積フロー配下で選択中患者IDを保持するルート。これ以外に遷移したら selectedPatientId をクリア
const estimateFlowRouteNames = new Set([
  'EstimatePatientList',
  'EstimateList',
  'EstimateCreate',
  'EstimateEdit',
]);

const showSelectPage = import.meta.env.VITE_VIEW_SELECT_PAGE === 'true';

const devRoutes = showSelectPage
  ? [
      {
        path: '/select',
        name: 'DevSelect',
        component: () => import('@/components/views/dev/DevSelect.vue'),
        meta: { title: 'ローカル開発メニュー' },
      },
      {
        path: '/dev/dummy-friend-added',
        name: 'DummyFriendAdded',
        component: () => import('@/components/views/dev/DummyFriendAdded.vue'),
        meta: { title: 'ダミー友だち追加完了' },
      },
      {
        path: '/dev/dummy-liff',
        name: 'DummyLiffSettings',
        component: () => import('@/components/views/dev/DummyLiffSettings.vue'),
        meta: { title: 'Dummy LIFF Settings' },
      },
    ]
  : [];

const routes = [
  {
    path: '/',
    redirect: () => {
      // pathクエリパラメータがあれば優先（Stripe決済後のLIFF戻りリンク等）
      const params = new URLSearchParams(window.location.search);
      const redirectPath = params.get('path');
      if (redirectPath && redirectPath.startsWith('/')) {
        return redirectPath;
      }

      // LIFF経由（liff.state クエリ付き）はホスト名に関係なく LIFF 扱い。
      // LIFFエンドポイントURLとWEB公開URLが同一ドメインの環境（dev/qa/stg/prod）で、
      // LIFFリダイレクトが WEB と誤判定されて web-login に飛ぶのを防ぐ。
      if (window.location.search.includes('liff.state')) {
        return '/patient/estimates';
      }

      // WEBアクセス（ドメインで判定）なら患者WEBログイン画面へ
      if (isWebAccess()) {
        if (localStorage.getItem('patient_access_token')) {
          return '/patient/estimates';
        }
        return '/patient/web-login';
      }

      // LIFF経由（LINE内ブラウザ）ならpatient側へ
      if (liff.isInClient?.()) {
        return '/patient/estimates';
      }
      return '/clinic/login';
    },
  },
  ...devRoutes,
  {
    path: '/stripe/return',
    name: 'StripeReturn',
    component: () => import('@/components/views/patient/StripeReturn.vue'),
    meta: { title: 'Stripe決済結果' },
  },
  ...clinicRoutes,
  ...patientRoutes,
  ...adminRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/components/views/NotFound.vue'),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, _from, next) => {
  const requiresAuth = to.meta.requiresAuth as boolean | undefined;
  const requiresAdminAuth = to.meta.requiresAdminAuth as boolean | undefined;
  const requiresPatientAuth = to.meta.requiresPatientAuth as boolean | undefined;
  const token = localStorage.getItem('access_token');
  const adminToken = localStorage.getItem('admin_access_token');
  const patientToken = localStorage.getItem('patient_access_token');

  if (!estimateFlowRouteNames.has(String(to.name ?? ''))) {
    useEstimateStore(pinia).clearSelectedPatientId();
  }

  if (requiresAdminAuth && !adminToken) {
    next({ name: 'AdminLogin' });
  } else if (requiresAuth && !token) {
    next({ name: 'ClinicLogin' });
  } else if (requiresPatientAuth && !patientToken) {
    // WEBログインが必要だがトークンが無い場合
    const loginType = localStorage.getItem('patient_login_type');
    if (loginType === 'mail') {
      next({ name: 'PatientWebLogin' });
    } else {
      next();
    }
  } else {
    next();
  }
});
